import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../api/client';
import { useNotify } from '../lib/notify';
import type { Contact } from './pickers';

/** Inline "new customer" form (name and an optional phone). */
export const quickCustomerSchema = z.object({
  name: z.string().trim().min(1, 'Enter the customer’s name').max(200),
  phone: z
    .string()
    .trim()
    .max(30)
    .refine((v) => v === '' || /^[+\d][\d\s-]{5,}$/.test(v), 'Enter a valid phone number'),
});
export type QuickCustomer = z.infer<typeof quickCustomerSchema>;

/** Create a customer without leaving the current screen (name and phone only). */
export function QuickCustomerDialog({
  open,
  initialName = '',
  initialPhone = '',
  onClose,
  onCreated,
}: {
  open: boolean;
  initialName?: string;
  initialPhone?: string;
  onClose: () => void;
  onCreated: (contact: Contact) => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const form = useForm<QuickCustomer>({
    resolver: zodResolver(quickCustomerSchema),
    values: { name: initialName, phone: initialPhone },
    mode: 'onBlur',
  });
  const create = useMutation({
    mutationFn: (v: QuickCustomer) =>
      unwrap(
        api.POST('/contacts', {
          params: { header: { 'idempotency-key': crypto.randomUUID() } },
          body: { name: v.name, phone: v.phone || null, isCustomer: true, isSupplier: false },
        }),
      ),
    onSuccess: async (contact) => {
      await qc.invalidateQueries({ queryKey: ['contacts'] });
      notify.success(`${contact.name} added`);
      onCreated(contact);
      form.reset();
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const d of e.details)
          if (d.path === 'name' || d.path === 'phone')
            form.setError(d.path, { message: d.message });
      notify.error(e);
    },
  });
  const { errors } = form.formState;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      // Focus once the dialog is open: opened from a search list, an autoFocus field would lose focus
      // to the closing list at once and show "Enter the customer’s name" before anything was typed.
      slotProps={{ transition: { onEntered: () => form.setFocus(initialName ? 'phone' : 'name') } }}
    >
      <form
        onSubmit={(e) => {
          // A picker inside another form (e.g. a sale) must not submit that form too.
          e.stopPropagation();
          void form.handleSubmit((v) => create.mutate(v))(e);
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New customer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              {...form.register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              label="Phone"
              {...form.register('phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message ?? 'Optional — used to find them next time'}
              slotProps={{ htmlInput: { inputMode: 'tel' } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={create.isPending}>
            Add customer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
